/* eslint-disable */
import React, { useEffect, useRef, useState } from 'react';
import { Comment, CommentThread } from '../../store/config/types/comment.types';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton, TextField } from '@mui/material';
import { Close, Send } from '@material-ui/icons';
import CommentBlob from './CommentBlob';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/config/types';
import CommentService from '../../services/comment.service';

interface CommentsDialogProps {
  commentsThread?: CommentThread;
  title?: string;
  // eslint-disable-next-line
  onThreadUpdated?: (updatedThread: CommentThread) => void;
  onClose: () => void;
}
function CommentsDialog({ commentsThread, title, onClose, onThreadUpdated }: CommentsDialogProps) {
  const user = useSelector((state: RootState) => state.auth?.account);
  const [newText, setNewText] = useState<string>('');
  const [comments, setComments] = useState<Comment[]>([]);
  const commentsDivRef = useRef<HTMLElement>();
  function isOwner(comment: Comment): boolean | undefined {
    if (!user) return false;
    return Boolean(
      comment.usersInfo?.length &&
        comment.usersInfo[0].type == 'SHIPPER_USER' &&
        comment.usersInfo[0].id == user.shipperUserId,
    );
  }

  function handleSendComment(): void {
    if (!commentsThread) return;
    const newCommentText = `${newText}`;
    setNewText('');
    CommentService.addCommentToThread(commentsThread.commentThreadId, newCommentText)
      .then((newComment) => {
        setComments((prevComments) => [...prevComments, newComment]);
        commentsThread.comments.push(newComment);
        if (onThreadUpdated) onThreadUpdated(commentsThread);
      })
      .catch(() => {
        setNewText(newCommentText);
      });
  }
  function handleClose() {
    setComments([]);
    setNewText('');
    onClose();
  }

  function scrollToEnd() {
    if (commentsDivRef.current) {
      commentsDivRef.current.scrollTo({ behavior: 'smooth', top: commentsDivRef.current.scrollHeight });
    }
    const unreadedCommensIds: number[] = [];
    comments.forEach((c) => {
      if (c.isNew) {
        unreadedCommensIds.push(c.commentId);
      }
    });
    if (commentsThread && unreadedCommensIds.length)
      CommentService.markAsRead(unreadedCommensIds).then(() => {
        onThreadUpdated &&
          onThreadUpdated({ ...commentsThread, comments: comments.map((c) => ({ ...c, isNew: false })) });
      });
  }

  useEffect(() => {
    scrollToEnd();
  }, [comments]);

  useEffect(() => {
    setComments(commentsThread?.comments ?? []);
  }, [commentsThread]);

  return (
    <Dialog open={Boolean(commentsThread)} onClose={handleClose}>
      <DialogTitle>
        <Grid container>
          <Grid item flexGrow={1}>
            {title ?? 'Comments'}
          </Grid>
          <Grid item>
            <IconButton size="small" onClick={handleClose}>
              <Close />
            </IconButton>
          </Grid>
        </Grid>
      </DialogTitle>
      <DialogContent ref={commentsDivRef}>
        <div style={{ minWidth: '400px' }}>
          {comments.map((comment) => (
            <CommentBlob comment={comment} isOwner={isOwner(comment)} />
          ))}
        </div>
      </DialogContent>
      <DialogActions>
        <Grid container>
          <Grid item flexGrow={1}>
            <TextField
              fullWidth
              size="small"
              value={newText}
              onChange={(e) => setNewText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key.toUpperCase() == 'ENTER') {
                  e.preventDefault();
                  handleSendComment();
                }
              }}
            />
          </Grid>
          <Grid item>
            <Button onClick={handleSendComment}>
              <Send />
            </Button>
          </Grid>
        </Grid>
      </DialogActions>
    </Dialog>
  );
}

export default React.memo(CommentsDialog, (prev, next) => {
  let equal = true;
  equal &&= prev.commentsThread?.commentThreadId == next.commentsThread?.commentThreadId;
  equal &&= prev.commentsThread?.comments?.length == next.commentsThread?.comments?.length;
  return equal;
});
